import { type BookmarkCacheUpdater, useToggleBookmark } from '@mdvip/shared/features/bookmarks/useToggleBookmark';
import { type QueryKey, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { type RecipeDetail, recipeKey } from './useRecipe';
import { type RecipesQueryResult, recipesKey } from './useRecipes';

/** The query entries a single toggle touched, with the data they held before the optimistic patch. */
type RecipeBookmarkSnapshot = Array<{ queryKey: QueryKey; data: unknown }>;

/**
 * Toggling a recipe's bookmark, with every cached surface it shows on kept in step: each list page
 * holding that recipe (so search and filtered results agree too) and its detail entry, in both
 * directions. Patching the cache rather than refetching is what makes a card and its detail page
 * carry the state over with no reload.
 */
export function useToggleRecipeBookmark() {
  const queryClient = useQueryClient();

  const cache = useMemo<BookmarkCacheUpdater<RecipeBookmarkSnapshot>>(
    () => ({
      apply(entityId, next) {
        const snapshot: RecipeBookmarkSnapshot = [];

        for (const [queryKey, data] of queryClient.getQueriesData<RecipesQueryResult>({ queryKey: recipesKey })) {
          if (!data) continue;
          const containsEntity = data.edges.some((edge) => edge.id === entityId);
          const hasSavedCount = typeof data.savedCount === 'number';
          // savedCount is the member's total bookmarked count, independent of this page's own
          // filter — every cached page carries its own copy, so a toggle from ANY page shifts it
          // on all of them, not just the one the toggle happened on.
          if (!containsEntity && !hasSavedCount) continue;

          snapshot.push({ queryKey, data });

          // The saved-only pages' own filter, read off this cache entry's own query key
          // ([...recipesKey, input]) rather than threaded through as a parameter — same shape
          // useRecipes.ts builds it with.
          const input = queryKey[2] as { bookmarkedOnly?: boolean } | undefined;
          const isSavedOnlyPage = input?.bookmarkedOnly === true;

          let edges = data.edges;
          let filteredCount = data.filteredCount;
          let totalCount = data.totalCount;

          if (containsEntity) {
            if (isSavedOnlyPage && !next) {
              // Drop the card out entirely rather than just flipping its flag — a saved-only page
              // showing an unsaved recipe is wrong, not just stale. totalCount shrinks too, which is
              // what lets ContentListing's own page > pageCount clamp step back a page on its own
              // (RecipesPage.tsx) — this updater only patches cache data, it doesn't navigate.
              edges = data.edges.filter((edge) => edge.id !== entityId);
              filteredCount -= 1;
              totalCount -= 1;
            } else {
              edges = data.edges.map((edge) => (edge.id === entityId ? { ...edge, isBookmarked: next } : edge));
            }
          }

          const savedCount = hasSavedCount ? (data.savedCount as number) + (next ? 1 : -1) : data.savedCount;

          queryClient.setQueryData<RecipesQueryResult>(queryKey, {
            ...data,
            edges,
            filteredCount,
            totalCount,
            savedCount,
          });
        }

        const detailKey = recipeKey(entityId);
        const detail = queryClient.getQueryData<RecipeDetail>(detailKey);
        if (detail) {
          snapshot.push({ queryKey: detailKey, data: detail });
          queryClient.setQueryData<RecipeDetail>(detailKey, { ...detail, isBookmarked: next });
        }

        return snapshot;
      },
      rollback(snapshot) {
        for (const { queryKey, data } of snapshot) queryClient.setQueryData(queryKey, data);
      },
      async invalidate(entityId) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: recipesKey }),
          queryClient.invalidateQueries({ queryKey: recipeKey(entityId) }),
        ]);
      },
    }),
    [queryClient],
  );

  return useToggleBookmark('NUTRITION_RECIPE', cache);
}
